import { useId, useRef, useState, type ChangeEvent } from 'react';
import { Check, FileText, Upload } from 'lucide-react';
import type {
  BrandAssets,
  BrandGuidelinesExtraction,
  Tenant,
  UpdateTenantDto,
  WorkspaceNameConflict,
} from '@mg/shared';
import { api, ApiRequestError, asWorkspaceNameConflict } from '../../lib/api';
import { cn } from '../../lib/cn';
import { useTenant } from '../../auth/TenantContext';
import { useIsWorkspaceAdmin } from '../../auth/useWorkspaceAdmin';
import { Button } from '../ui/Button';
import { Card, CardBody, CardHeader } from '../ui/Card';
import { FormField, Input, Textarea } from '../ui/Field';
import { BrandAssetsEditor } from './BrandAssetsEditor';
import { initBrandAssets } from './brandAssetsDefaults';
import { WorkspaceNameConflictNotice } from './WorkspaceNameConflictNotice';

const BRAND_VOICE_HINT =
  "A system prompt that defines your brand's goals and guidelines for the AI — the strongest, most general voice guidance.";
const COMPANY_VOICE_HINT =
  "The organization's positioning and messaging for the AI — refines the Brand Voice above. Shared across the workspace.";

const GUIDELINES_ACCEPT =
  '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** Inline result of a brand-guidelines extraction, rendered under the uploader. */
type ExtractFeedback =
  | { kind: 'success'; text: string }
  | { kind: 'empty'; text: string }
  | { kind: 'error'; text: string };

/**
 * Deterministic JSON with recursively-sorted object keys. `brandAssets` is persisted in a Postgres
 * `jsonb` column, which reorders object keys on round-trip — so comparing the raw `JSON.stringify`
 * of a locally-built object (JS insertion order) against the re-fetched value would read as "dirty"
 * forever after a save (or on a fresh workspace / a newly-added color). Sorting keys makes the
 * dirty check order-insensitive. Mirrors JSON semantics: undefined object values are dropped,
 * undefined array items serialize as null.
 */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((v) => stableStringify(v)).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj)
      .filter((k) => obj[k] !== undefined)
      .sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

/**
 * Edit the active Brand Workspace, split into two independently-saved sections:
 * "Brand Workspace Details" (name / website / description / Brand Voice / Company Voice) and
 * "Brand Assets & Style" (guidelines upload + colors, typography, hashtags, phrases, logos).
 * Each section owns its dirty state + Save button and PATCHes ONLY its own fields; on success it
 * refreshes the tenant list (so the `workspace` prop flows back in updated) and shows a brief
 * "Saved" indicator rather than navigating away. Each Save stays disabled until that section is
 * actually dirty relative to the current `workspace` prop.
 */
export function EditWorkspaceForm({
  workspace,
  onCancel,
}: {
  workspace: Tenant;
  onCancel: () => void;
}) {
  const { refresh } = useTenant();
  // Members have read-only access to workspace settings (the API also 403s their PATCH).
  const canEdit = useIsWorkspaceAdmin();

  const baseId = useId();
  const nameId = `${baseId}-name`;
  const websiteId = `${baseId}-website`;
  const descId = `${baseId}-desc`;
  const voiceId = `${baseId}-voice`;
  const companyVoiceId = `${baseId}-company-voice`;
  const guidelinesId = `${baseId}-guidelines`;

  const [name, setName] = useState(workspace.name);
  const [websiteUrl, setWebsiteUrl] = useState(workspace.websiteUrl ?? '');
  const [description, setDescription] = useState(workspace.description ?? '');
  const [brandVoice, setBrandVoice] = useState(workspace.brandVoice ?? '');
  const [companyVoice, setCompanyVoice] = useState(workspace.companyVoice ?? '');
  const [brandAssets, setBrandAssets] = useState<BrandAssets>(() =>
    initBrandAssets(workspace.brandAssets),
  );

  // Each section saves independently, so it tracks its own in-flight + "Saved" state.
  const [savingDetails, setSavingDetails] = useState(false);
  const [savedDetails, setSavedDetails] = useState(false);
  const [savingAssets, setSavingAssets] = useState(false);
  const [savedAssets, setSavedAssets] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<WorkspaceNameConflict | null>(null);

  const guidelinesRef = useRef<HTMLInputElement>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractFeedback, setExtractFeedback] = useState<ExtractFeedback | null>(null);

  const trimmedName = name.trim();
  const busy = savingDetails || savingAssets;

  // Compare live form state against the current `workspace` prop each render, per section, so
  // after a successful save (which refreshes the prop) that section is no longer dirty and its
  // button returns to disabled.
  const detailsDirty =
    trimmedName !== workspace.name ||
    websiteUrl.trim() !== (workspace.websiteUrl ?? '') ||
    description.trim() !== (workspace.description ?? '') ||
    brandVoice.trim() !== (workspace.brandVoice ?? '') ||
    companyVoice.trim() !== (workspace.companyVoice ?? '');
  const assetsDirty =
    stableStringify(brandAssets) !== stableStringify(initBrandAssets(workspace.brandAssets));

  const detailsCanSubmit = canEdit && trimmedName.length > 0 && !savingDetails && detailsDirty;
  const assetsCanSubmit = canEdit && !savingAssets && assetsDirty;

  async function saveDetails() {
    if (!detailsCanSubmit) return;
    setSavingDetails(true);
    setSavedDetails(false);
    setError(null);
    setConflict(null);
    try {
      const dto: UpdateTenantDto = {
        name: trimmedName,
        websiteUrl: websiteUrl.trim(),
        description: description.trim(),
        brandVoice: brandVoice.trim(),
        companyVoice: companyVoice.trim(),
      };
      await api.updateTenant(workspace.id, dto);
      await refresh();
      setSavedDetails(true);
    } catch (err) {
      const nameConflict = asWorkspaceNameConflict(err);
      if (nameConflict) {
        setConflict(nameConflict);
      } else {
        setError(
          err instanceof ApiRequestError
            ? err.message
            : 'Could not save the workspace details. The API may be unreachable — try again.',
        );
      }
    } finally {
      setSavingDetails(false);
    }
  }

  async function saveAssets() {
    if (!assetsCanSubmit) return;
    setSavingAssets(true);
    setSavedAssets(false);
    setError(null);
    try {
      await api.updateTenant(workspace.id, { brandAssets });
      await refresh();
      setSavedAssets(true);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : 'Could not save the brand assets. The API may be unreachable — try again.',
      );
    } finally {
      setSavingAssets(false);
    }
  }

  /**
   * Extract suggested fields from an uploaded brand-guidelines doc and PREFILL the
   * Brand Workspace Details for review. Any returned key overwrites the matching field (marking
   * the Details section dirty); we never auto-save — the user reviews and clicks Save details.
   */
  async function handleGuidelinesFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setExtracting(true);
    setExtractFeedback(null);
    setError(null);
    try {
      const result = await api.extractBrandGuidelines(workspace.id, file);
      applyExtraction(result);
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : 'Could not read that file — please try a different PDF or Word document.';
      setExtractFeedback({ kind: 'error', text: message });
    } finally {
      setExtracting(false);
    }
  }

  function applyExtraction(result: BrandGuidelinesExtraction) {
    let prefilled = false;
    if (result.name !== undefined) {
      setName(result.name);
      prefilled = true;
    }
    if (result.description !== undefined) {
      setDescription(result.description);
      prefilled = true;
    }
    if (result.brandVoice !== undefined) {
      setBrandVoice(result.brandVoice);
      prefilled = true;
    }
    if (result.companyVoice !== undefined) {
      setCompanyVoice(result.companyVoice);
      prefilled = true;
    }
    if (prefilled) {
      setSavedDetails(false);
      setExtractFeedback({
        kind: 'success',
        text: 'Prefilled the Brand Workspace Details above — review and Save details.',
      });
    } else {
      setExtractFeedback({
        kind: 'empty',
        text: "Couldn't extract fields from that document — fill them in manually.",
      });
    }
  }

  return (
    <div className="space-y-4">
      {!canEdit && (
        <div className="rounded-lg border border-line bg-page px-4 py-3 text-sm text-ink-label">
          You have read-only access to this workspace&apos;s settings. Ask a workspace admin to
          make changes.
        </div>
      )}

      {/* ── Brand Workspace Details ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader
          title="Brand Workspace Details"
          description="This workspace's name and website, plus the voice guidance the AI writes with."
        />
        <CardBody className="space-y-4">
          <FormField label="Brand Workspace Name" htmlFor={nameId}>
            <Input
              id={nameId}
              value={name}
              onChange={(e) => {
                setSavedDetails(false);
                setConflict(null);
                setName(e.target.value);
              }}
              required
              maxLength={120}
              disabled={!canEdit || savingDetails}
            />
          </FormField>

          <FormField label="Public Website" htmlFor={websiteId}>
            <Input
              id={websiteId}
              type="url"
              inputMode="url"
              placeholder="https://www.yourbrand.com"
              value={websiteUrl}
              onChange={(e) => {
                setSavedDetails(false);
                setWebsiteUrl(e.target.value);
              }}
              disabled={!canEdit || savingDetails}
            />
          </FormField>

          <FormField label="Brand Description" htmlFor={descId}>
            <Textarea
              id={descId}
              value={description}
              onChange={(e) => {
                setSavedDetails(false);
                setDescription(e.target.value);
              }}
              rows={3}
              disabled={!canEdit || savingDetails}
            />
          </FormField>

          <FormField label="Brand Voice" htmlFor={voiceId} hint={BRAND_VOICE_HINT}>
            <Textarea
              id={voiceId}
              value={brandVoice}
              onChange={(e) => {
                setSavedDetails(false);
                setBrandVoice(e.target.value);
              }}
              rows={6}
              disabled={!canEdit || savingDetails}
            />
          </FormField>

          <FormField label="Company Voice" htmlFor={companyVoiceId} hint={COMPANY_VOICE_HINT}>
            <Textarea
              id={companyVoiceId}
              value={companyVoice}
              onChange={(e) => {
                setSavedDetails(false);
                setCompanyVoice(e.target.value);
              }}
              rows={6}
              disabled={!canEdit || savingDetails}
            />
          </FormField>

          {conflict && <WorkspaceNameConflictNotice conflict={conflict} />}

          <div className="flex items-center justify-end gap-3 pt-1">
            {savedDetails && !detailsDirty && (
              <span className="flex items-center gap-1 text-sm text-success dark:text-success-dark">
                <Check className="h-4 w-4" />
                Saved
              </span>
            )}
            <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
              {canEdit ? 'Cancel' : 'Back'}
            </Button>
            {canEdit && (
              <Button
                type="button"
                loading={savingDetails}
                disabled={!detailsCanSubmit}
                onClick={saveDetails}
              >
                Save details
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      {/* ── Brand Assets & Style ────────────────────────────────────────────────── */}
      <Card>
        <CardHeader
          title="Brand Assets & Style"
          description="Upload brand guidelines to auto-fill, plus color schemes, typography, approved hashtags and phrases, and logos."
        />
        <CardBody className="space-y-4">
          <div className="space-y-2 rounded-lg border border-line bg-page p-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-action" aria-hidden />
              <h3 className="text-sm font-semibold text-ink-strong">
                Upload Brand Guideline Document(s)
              </h3>
            </div>
            <p className="text-xs text-ink-muted">
              Upload a PDF or Word doc of your brand guidelines to auto-fill the Brand Workspace
              Details above.
            </p>
            <label htmlFor={guidelinesId} className="sr-only">
              Upload brand guidelines
            </label>
            <input
              ref={guidelinesRef}
              id={guidelinesId}
              type="file"
              accept={GUIDELINES_ACCEPT}
              className="sr-only"
              onChange={handleGuidelinesFile}
              disabled={extracting || busy || !canEdit}
            />
            <div className="flex items-center gap-3">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                loading={extracting}
                disabled={extracting || busy || !canEdit}
                onClick={() => guidelinesRef.current?.click()}
              >
                <Upload className="h-3.5 w-3.5" aria-hidden />
                Upload guidelines
              </Button>
              {extracting && <span className="text-xs text-ink-muted">Reading your guidelines…</span>}
            </div>
            {extractFeedback && (
              <p
                role={extractFeedback.kind === 'error' ? 'alert' : undefined}
                className={cn(
                  'text-xs',
                  extractFeedback.kind === 'success' && 'text-success dark:text-success-dark',
                  extractFeedback.kind === 'empty' && 'text-amber-600 dark:text-warning-dark',
                  extractFeedback.kind === 'error' && 'text-red-600 dark:text-error-dark',
                )}
              >
                {extractFeedback.text}
              </p>
            )}
          </div>

          <BrandAssetsEditor
            value={brandAssets}
            onChange={(next) => {
              setSavedAssets(false);
              setBrandAssets(next);
            }}
            disabled={savingAssets || !canEdit}
          />

          {canEdit && (
            <div className="flex items-center justify-end gap-3 pt-1">
              {savedAssets && !assetsDirty && (
                <span className="flex items-center gap-1 text-sm text-success dark:text-success-dark">
                  <Check className="h-4 w-4" />
                  Saved
                </span>
              )}
              <Button
                type="button"
                loading={savingAssets}
                disabled={!assetsCanSubmit}
                onClick={saveAssets}
              >
                Save brand assets
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {error && (
        <p role="alert" className="text-sm text-red-600 dark:text-error-dark">
          {error}
        </p>
      )}
    </div>
  );
}
